import { create } from '@bufbuild/protobuf';
import { timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import {
  LoginMaintenanceCriteria,
  LoginMaintenanceCriteriaSchema,
} from './espb/login_maintenance_criteria_pb';
import {
  VaultService,
  VaultCreationDataSchema,
  VaultRegenerateDataSchema,
  VaultPairCreationDataSchema,
} from './espb/vault_service_pb';
import type {
  VaultCreationData,
  VaultRegenerateData,
  VaultPairCreationData,
} from './espb/vault_service_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  CommitDto,
  CommitId,
  VaultId,
  VaultUpdateDto,
  RegeneratedVaultDto,
  SyncUpdate,
  VaultPairCreationDto,
  GenerationId,
  VaultProfileLockDto,
  OrganizationId,
  ProfileId,
  VaultCreationDto,
  toVaultProfileLockDto,
  toPbVaultProfileLock,
  VaultMessageId,
  VaultMessageDto,
  ShareLinkUpdateDto,
  ShareLinkId,
  ShareSecretUpdateDto,
  ShareSecretId,
  LoginMaintenanceCriteriaDto,
  VaultMemberSyncMode,
  EntraGroupId,
  VaultPermissionLevel,
  GrpcTimestamp,
} from './types';

export default class VaultServiceClient {
  constructor(private readonly espbClient: Client<typeof VaultService>) {}

  async createCommit({
    deleteMessageIds,
    latestCommitId,
    newCommitBlob,
    shareLinkUpdates,
    shareSecretUpdates,
    updateMessages,
    updateTime,
    vaultId,
    deleteShareLinks,
    deleteShareSecrets,
    loginMaintenanceCriteria,
  }: {
    vaultId: VaultId;
    latestCommitId: CommitId;
    newCommitBlob: Uint8Array;
    updateTime: Date;
    deleteMessageIds?: VaultMessageId[];
    updateMessages?: Array<VaultMessageDto>;
    shareLinkUpdates?: ShareLinkUpdateDto[];
    deleteShareLinks?: { keepShareLinkIds: ShareLinkId[] };
    shareSecretUpdates?: ShareSecretUpdateDto[];
    deleteShareSecrets?: { keepShareSecretIds: ShareSecretId[] };
    loginMaintenanceCriteria?: LoginMaintenanceCriteriaDto;
  }): Promise<{
    commitId: CommitId;
    updateMessageIds: VaultMessageId[];
  }> {
    const resp = await this.espbClient.createCommit({
      vaultId,
      latestCommitId,
      newCommitBlob,
      updateTime: timestampFromDate(updateTime),
      deleteMessageIds,
      updateMessages,
      shareLinkUpdates: shareLinkUpdates?.map((slu) => ({
        ...slu,
        expiresAt: slu.expiresAt ? timestampFromDate(slu.expiresAt) : undefined,
      })),
      deleteShareLinks,
      shareSecretUpdates,
      deleteShareSecrets,
      loginMaintenanceCriteria:
        loginMaintenanceCriteria ?
          toPbLoginMaintenanceCriteria(loginMaintenanceCriteria)
        : undefined,
    });

    return {
      commitId: requireFieldNotEmpty(resp.commitId),
      updateMessageIds: resp.updateMessageIds,
    };
  }

  async createGeneration({
    dto,
    updateMessages,
    deleteShareLinks,
    shareLinkUpdates,
    shareSecretUpdates,
    deleteShareSecrets,
    loginMaintenanceCriteria,
  }: {
    dto: RegeneratedVaultDto;
    updateMessages?: Array<VaultMessageDto>;
    deleteShareLinks?: { keepShareLinkIds: ShareLinkId[] };
    shareLinkUpdates?: ShareLinkUpdateDto[];
    shareSecretUpdates?: ShareSecretUpdateDto[];
    deleteShareSecrets?: { keepShareSecretIds: ShareSecretId[] };
    loginMaintenanceCriteria?: LoginMaintenanceCriteriaDto;
  }): Promise<{
    syncUpdate: SyncUpdate;
    updateMessageIds: VaultMessageId[];
  }> {
    const resp = await this.espbClient.createGeneration({
      vault: toVaultRegenerateData(dto),
      updateMessages,
      deleteShareLinks,
      shareLinkUpdates: shareLinkUpdates?.map((slu) => ({
        ...slu,
        expiresAt: slu.expiresAt ? timestampFromDate(slu.expiresAt) : undefined,
      })),
      shareSecretUpdates,
      deleteShareSecrets,
      loginMaintenanceCriteria:
        loginMaintenanceCriteria ?
          toPbLoginMaintenanceCriteria(loginMaintenanceCriteria)
        : undefined,
    });

    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      updateMessageIds: resp.updateMessageIds,
    };
  }

  async listCommits(
    vaultId: VaultId,
    latestCommitId: CommitId | undefined,
    latestFirstCommitId: CommitId | undefined,
    forceLocks: boolean,
  ): Promise<VaultUpdateDto> {
    const resp = await this.espbClient.listCommits({
      vaultId,
      latestCommitId,
      latestFirstCommitId,
      forceLocks,
    });

    const { commitDtos } = resp.newerCommits.reduce(
      ({ parentId, commitDtos: dtos }, commit) => ({
        parentId: requireFieldNotEmpty(commit.id),
        commitDtos: [
          ...dtos,
          {
            id: requireFieldNotEmpty(commit.id),
            parentId,
            blob: commit.blob,
          },
        ],
      }),
      { parentId: latestCommitId, commitDtos: [] as CommitDto[] },
    );

    const profileLockDto = toVaultProfileLockDto(resp.profileLock);
    const adminProfileLockDto = toVaultProfileLockDto(resp.adminProfileLock);
    const messagePublicKey = resp.messagePublicKey.length > 0 ? resp.messagePublicKey : undefined;

    const serverTime = timestampDate(requireFieldNotEmpty(resp.serverTime));
    const firstCommitId = requireFieldNotEmpty(resp.firstCommitId);

    const messages = resp.messages.map((im) => {
      return {
        id: requireFieldNotEmpty(im.id),
        type: requireFieldNotEmpty(im.type),
        senderVaultId: im.senderVaultId,
        encryptedData: im.encryptedData.length > 0 ? im.encryptedData : undefined,
        backendMessage: im.backendMessage.length > 0 ? im.backendMessage : undefined,
      };
    });

    return {
      generationId: requireFieldNotEmpty(resp.currentGenerationId),
      commitDtos,
      profileLockDto,
      adminProfileLockDto,
      messagePublicKey,
      serverTime,
      firstCommitId,
      messages,
      loginMaintenanceCriteria:
        resp.loginMaintenanceCriteria ?
          toLoginMaintenanceCriteriaDto(resp.loginMaintenanceCriteria)
        : undefined,
    };
  }

  async createTeam(
    vcd: VaultPairCreationDto,
    memberSyncMode: VaultMemberSyncMode = VaultMemberSyncMode.NONE,
    entraGroupId?: EntraGroupId,
  ): Promise<{
    vaultId: VaultId;
    associatedVaultId: VaultId;
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.createTeam({
      creationData: toVaultPairCreationData(vcd),
      memberSyncMode,
      entraGroupId,
    });
    return {
      vaultId: requireFieldNotEmpty(resp.vaultId),
      associatedVaultId: requireFieldNotEmpty(resp.associatedVaultId),
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async deleteTeam(vaultId: VaultId): Promise<{
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.deleteTeam({ vaultId });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async updateVault(
    vaultId: VaultId,
    { memberSyncMode }: { memberSyncMode?: VaultMemberSyncMode },
  ): Promise<{
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.update({
      vaultId,
      memberSyncMode: memberSyncMode !== undefined ? { value: memberSyncMode } : undefined,
    });
    return { syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)) };
  }

  async modifyTeamUsers(
    id: VaultId,
    generationId: GenerationId,
    addProfiles: Array<{
      id: ProfileId;
      lock: VaultProfileLockDto;
      associatedLock?: VaultProfileLockDto;
    }>,
    removeProfileIds: ProfileId[],
    assocGenerationId?: GenerationId,
    assocLatestCommitId?: CommitId,
    assocNewCommitBlob?: Uint8Array,
    updateTime?: Date,
    regenerateData?: RegeneratedVaultDto,
    associatedRegenerateData?: RegeneratedVaultDto,
    memberSyncTime?: GrpcTimestamp,
  ): Promise<{
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.modifyTeamUsers({
      vaultId: id,
      generationId,
      assocGenerationId,
      addProfileOps: addProfiles.map((p) => ({
        profileId: p.id,
        lock: toPbVaultProfileLock(p.lock),
        associatedLock: p.associatedLock ? toPbVaultProfileLock(p.associatedLock) : undefined,
      })),
      removeProfileIds,
      assocLatestCommitId,
      assocNewCommitBlob,
      updateTime: updateTime ? timestampFromDate(updateTime) : undefined,
      regenerateData: regenerateData ? toVaultRegenerateData(regenerateData) : undefined,
      associatedRegenerateData:
        associatedRegenerateData ? toVaultRegenerateData(associatedRegenerateData) : undefined,
      memberSyncTime,
    });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async createInboxVault(
    vcd: VaultPairCreationDto,
    metadataJson: string,
  ): Promise<{ syncUpdate: SyncUpdate; vaultId: VaultId; associatedVaultId: VaultId }> {
    const resp = await this.espbClient.createInboxVault({
      creationData: toVaultPairCreationData(vcd),
      metadata: metadataJson,
    });

    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      vaultId: requireFieldNotEmpty(resp.vaultId),
      associatedVaultId: requireFieldNotEmpty(resp.associatedVaultId),
    };
  }

  async updateInbox(
    vaultId: VaultId,
    { metadataJson, enabled }: { metadataJson?: string; enabled?: boolean },
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.updateInbox({
      vaultId,
      metadata: metadataJson ? metadataJson : undefined,
      enabled,
    });

    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async deleteInboxVault(vaultId: VaultId): Promise<SyncUpdate> {
    const resp = await this.espbClient.deleteInboxVault({ vaultId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async listMessagePublicKeys(
    organizationId: OrganizationId,
    vaultIds: VaultId[],
  ): Promise<{
    messagePublicKeys: {
      vaultId: VaultId;
      generationId: GenerationId;
      key: Uint8Array;
    }[];
  }> {
    return this.espbClient.listMessagePublicKeys({ organizationId, vaultIds });
  }

  async getSyncMemberTargetState(vaultId: VaultId): Promise<{
    members: {
      profileId: ProfileId;
      defaultPermission: VaultPermissionLevel;
      allowedPermissions: VaultPermissionLevel[];
    }[];
    syncTime: GrpcTimestamp;
    vaultName: string;
  }> {
    const resp = await this.espbClient.getSyncMemberTargetState({ vaultId });
    return {
      members: resp.members.map((m) => ({
        profileId: m.profileId,
        defaultPermission: knownEnumOrDefault(m.defaultPermission, VaultPermissionLevel),
        allowedPermissions: m.allowedPermissions.map((p) =>
          knownEnumOrDefault(p, VaultPermissionLevel),
        ),
      })),
      syncTime: requireFieldNotEmpty(resp.syncTime),
      vaultName: resp.vaultName,
    };
  }
}

function toPbLoginMaintenanceCriteria(
  loginMaintenanceCriteria: LoginMaintenanceCriteriaDto,
): LoginMaintenanceCriteria {
  return create(LoginMaintenanceCriteriaSchema, {
    noLogin: loginMaintenanceCriteria.noLogin,
    leakedPasswordsMinChangeTime:
      loginMaintenanceCriteria.leakedPasswordsMinChangeTime ?
        timestampFromDate(loginMaintenanceCriteria.leakedPasswordsMinChangeTime)
      : undefined,
    loginSummaryMinVersion: loginMaintenanceCriteria.loginSummaryMinVersion,
    passwordPolicyMinChangeTime:
      loginMaintenanceCriteria.passwordPolicyMinChangeTime ?
        timestampFromDate(loginMaintenanceCriteria.passwordPolicyMinChangeTime)
      : undefined,
  });
}

function toLoginMaintenanceCriteriaDto(
  loginMaintenanceCriteria: LoginMaintenanceCriteria,
): LoginMaintenanceCriteriaDto {
  return {
    noLogin: loginMaintenanceCriteria?.noLogin,
    leakedPasswordsMinChangeTime:
      loginMaintenanceCriteria.leakedPasswordsMinChangeTime ?
        timestampDate(loginMaintenanceCriteria.leakedPasswordsMinChangeTime)
      : undefined,
    loginSummaryMinVersion: loginMaintenanceCriteria.loginSummaryMinVersion || undefined,
    passwordPolicyMinChangeTime:
      loginMaintenanceCriteria.passwordPolicyMinChangeTime ?
        timestampDate(loginMaintenanceCriteria.passwordPolicyMinChangeTime)
      : undefined,
  };
}

export function toPbVaultCreationData({
  blob,
  profileLock,
  adminProfileLock,
  organizationId,
  messagePublicKey,
}: VaultCreationDto): VaultCreationData {
  return create(VaultCreationDataSchema, {
    firstCommitBlob: blob,
    profileLock: profileLock ? toPbVaultProfileLock(profileLock) : undefined,
    adminProfileLock: adminProfileLock ? toPbVaultProfileLock(adminProfileLock) : undefined,
    organizationId,
    messagePublicKey,
  });
}

export function toVaultPairCreationData({
  blob,
  profileLock,
  adminProfileLock,
  messagePublicKey,
  associatedBlob,
  associatedProfileLock,
  associatedAdminProfileLock,
  associatedMessagePublicKey,
  organizationId,
}: VaultPairCreationDto): VaultPairCreationData {
  return create(VaultPairCreationDataSchema, {
    firstCommitBlob: blob,
    profileLock: profileLock ? toPbVaultProfileLock(profileLock) : undefined,
    adminProfileLock: adminProfileLock ? toPbVaultProfileLock(adminProfileLock) : undefined,
    messagePublicKey,
    associatedFirstCommitBlob: associatedBlob,
    associatedProfileLock:
      associatedProfileLock ? toPbVaultProfileLock(associatedProfileLock) : undefined,
    associatedAdminProfileLock:
      associatedAdminProfileLock ? toPbVaultProfileLock(associatedAdminProfileLock) : undefined,
    associatedMessagePublicKey,
    organizationId,
  });
}

export function toVaultRegenerateData(dto: RegeneratedVaultDto): VaultRegenerateData {
  const vault = create(VaultRegenerateDataSchema, {
    ...dto,
    squashedCommits: dto.blob,
    profileLocks: dto.profileLocks.map(toPbVaultProfileLock),
  });
  return vault;
}
